import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  GridTable,
  SecondaryHeader,
  SecondaryHeaderButtonWrap,
  SecondaryHeaderInfo,
  SecondaryHeaderSearchField,
  Spinner,
} from "olive-react-myoneui";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PaginationTemplate from "../component/grid-table/PaginationTemplate";
import TextTemplate from "../component/grid-table/TextTemplate";
import { penEditIcon, removeIcon } from "../../../constants/icons";
import ConfirmBox from "../component/ConfirmBox";
import AddOffcanvas from "./AddOffcanvas";
import EditOffcanvas from "./EditOffcanvas";
import {
  createTopic,
  getTopic,
  deleteTopic,
  updateTopic,
} from "../../../api/topicApis";
import { debounce } from "../../../utils/helper";
import BasicContentLoader from "../../../components/loader/BasicContentLoader";
import { getSubject } from "../../../api/subjectApis";
import DataNotFound from "../component/DataNotFound";

export default function Topics() {
  const [showConfirmBox, setShowConfirmBox] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);

  const [data, setData] = useState([]);
  const [topicTitle, setTopicTitle] = useState("");
  const [topicId, setTopicId] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState("");
  const [filter, setFilter] = useState(null);

  const apiCallInProgress = useRef(false);
  const tableManager = useRef(null);
  const setTableManager = (tm) => (tableManager.current = tm);

  const debouncedSetSearchText = useMemo(
    () =>
      debounce((val) => {
        setSearchText(val);
        setPage(1);
      }, 500),
    [],
  );

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    debouncedSetSearchText(val);
  };

  const onClearSearch = () => {
    setSearch("");
    setSearchText("");
    setPage(1);
  };

  function formatDate(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    return date
      .toLocaleString("en-GB", options)
      .replace("AM", "am")
      .replace("PM", "pm");
  }
  const fetchTopics = async () => {
    if (apiCallInProgress.current) return;
    apiCallInProgress.current = true;
    setLoading(true);

    try {
      const params = {
        page,
        limit: pageSize,
        ...(searchText ? { title: searchText } : {}),
      };

      const res = await getTopic(params);
      if (res?.data) {
        const formatted = res.data.data.map((t) => ({
          ...t,
          subject_topic: t.subject?.title || "",
          createdAt: formatDate(t.createdAt),
        }));

        setData(formatted);
        setTotalItems(res.data.pagination.totalItems || 0);
        setPageSize(res.data.pagination.pageSize || pageSize);
      }
    } catch (e) {
      setData([]);
      setTotalItems(0);
      console.error(e);
      toast.error(
        e?.response?.data?.message ||
          "Failed to fetch topics. Please try again.",
      );
    } finally {
      setLoading(false);
      apiCallInProgress.current = false;
    }
  };

  useEffect(() => {
    async function fetchSubjects() {
      try {
        const res = await getSubject();
        setSubjects(res?.data?.data || []);
      } catch (e) {
        console.error(e);
        toast.error(
          e?.response?.data?.message ||
            "Failed to fetch subjects. Please try again.",
        );
      }
    }
    fetchSubjects();
  }, []);

  useEffect(() => {
    async function fetchTopics() {
      if (apiCallInProgress.current) return;
      apiCallInProgress.current = true;
      setLoading(true);

      try {
        const params = {
          page,
          limit: pageSize,
          ...(searchText ? { title: searchText } : {}),
        };

        const res = await getTopic(params);
        if (res?.data) {
          const formatted = res.data.data.map((t) => ({
            ...t,
            subject_topic: t.subject?.title || "",
            createdAt: formatDate(t.createdAt),
          }));

          setData(formatted);
          setTotalItems(res.data.pagination.totalItems || 0);
          setPageSize(res.data.pagination.pageSize || pageSize);
        }
      } catch (e) {
        setData([]);
        setTotalItems(0);
        console.error(e);
        toast.error(
          e?.response?.data?.message ||
            "Failed to fetch topics. Please try again.",
        );
      } finally {
        setLoading(false);
        apiCallInProgress.current = false;
      }
    }
    fetchTopics();
  }, [page, pageSize, searchText]);

  const handleEditTopic = (item) => {
    setTopicId(item._id);
    setTopicTitle(item.title);

    if (item.subject) {
      setSelectedSubject({
        _id: item.subject?._id,
        name: item.subject?.title,
      });
    } else {
      setSelectedSubject(null);
    }

    setShowEdit(true);
  };

  const handleDeleteTopic = (id) => {
    setTopicId(id);
    setShowConfirmBox(true);
  };

  const deleteTopicById = async () => {
    setLoading(true);
    try {
      const res = await deleteTopic(topicId);
      if (res?.status === 200) {
        setShowConfirmBox(false);
        setTopicId(null);
        setPage(1);
        toast.success("Topic deleted successfully.");
        await fetchTopics();
      }
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.message ||
          "Failed to delete topic. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowAdd(false);
    setShowEdit(false);
    setTopicTitle("");
    setSelectedSubject(null);
    setTopicId(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = { title: topicTitle, subject: selectedSubject?._id };
      const res = await createTopic(payload);
      if (res) {
        handleClose();
        setPage(1);
        toast.success("Topic added successfully.");
        await fetchTopics();
      }
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.message ||
          "Failed to create topic. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const updateTopics = async (title, subjectId, id) => {
    setLoading(true);
    try {
      const payload = { title, subject: subjectId };
      const res = await updateTopic(payload, id);
      if (res) {
        handleClose();
        toast.success("Topic updated successfully.");
        await fetchTopics();
      }
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.message ||
          "Failed to update topic. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (!loading) setPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    if (!loading && newSize !== pageSize) {
      setPageSize(newSize);
      setPage(1);
    }
  };

  const columnData = [
    {
      id: 0,
      visible: true,
      label: "S No.",
      width: "1fr",
      sortable: false,
      className: "grid-table-title",
      cellRenderer: ({ rowIndex }) => <TextTemplate value={rowIndex} />,
    },
    {
      id: 1,
      field: "title",
      label: "Topics",
      width: "3fr",
      sortable: false,
      cellRenderer: TextTemplate,
    },
    {
      id: 2,
      field: "subject_topic",
      label: "Subjects Title",
      width: "3fr",
      sortable: false,
      cellRenderer: ({ data }) => <TextTemplate value={data?.subject?.title} />,
    },

    {
      id: 3,
      field: "createdAt",
      label: "Created At",
      width: "3fr",
      sortable: false,
      className: "grid-table-title",
      cellRenderer: TextTemplate,
    },
    {
      id: 4,
      label: "Action",
      width: "2fr",
      sortable: false,
      className: "grid-header-text-center",
      cellRenderer: ({ data }) => (
        <div className="rgt-cell-inner program-edit-hover justify-content-center">
          <Button
            variant="white"
            className="btn-round"
            onClick={() => handleEditTopic(data)}
            disabled={loading}
          >
            {penEditIcon} Edit
          </Button>
          <Button
            variant="white"
            className="btn-round"
            onClick={() => handleDeleteTopic(data._id)}
            disabled={loading}
          >
            {removeIcon} Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="content-wrapper position-relative">
        <SecondaryHeader>
          <SecondaryHeaderInfo
            SecondaryHeaderTitle="Topics"
            SecondaryHeaderText={`${totalItems} Topics`}
            SecondaryText
          />
          <SecondaryHeaderSearchField
            placeholder="Search Topics..."
            value={search}
            onChange={onSearchChange}
            onClear={onClearSearch}
            disabled={loading}
          />
          <SecondaryHeaderButtonWrap>
            <Button
              variant="secondary"
              onClick={() => setShowAdd(true)}
              disabled={loading}
            >
              Add
            </Button>
          </SecondaryHeaderButtonWrap>
        </SecondaryHeader>

        {/* Loader Overlay */}
        {loading && (
          <div className="spinner-wrapper">
            <Spinner animation="border" />
          </div>
        )}

        {!loading && (
          <div className="content-block p-2">
            {data.length > 0 ? (
              <GridTable
                key={data.length}
                searchText={searchText}
                highlightSearch={false}
                columns={columnData}
                enableColumnsReorder={false}
                onRowsRequest={() => ({
                  rows: data,
                  totalRows: totalItems,
                })}
                page={page}
                pageSize={pageSize}
                setPage={handlePageChange}
                setPageSize={handlePageSizeChange}
                totalPages={Math.ceil(totalItems / pageSize)}
                filter={filter}
                onLoad={setTableManager}
                components={{
                  Footer: (props) => (
                    <PaginationTemplate
                      {...props}
                      page={page}
                      pageSize={pageSize}
                      totalPages={Math.ceil(totalItems / pageSize)}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      disabled={loading}
                    />
                  ),
                }}
              />
            ) : (
              <DataNotFound title="No topics found" nochange={true} />
            )}
          </div>
        )}
      </div>

      <ConfirmBox
        show={showConfirmBox}
        question="Are you sure you want to delete topics?"
        okText="Delete"
        cancelText="Cancel"
        onHide={() => setShowConfirmBox(false)}
        onSubmit={deleteTopicById}
      />

      <AddOffcanvas
        show={showAdd}
        subjects={subjects}
        topics={topicTitle}
        setTopics={setTopicTitle}
        handleSubmit={handleSubmit}
        selectedSubject={selectedSubject}
        setSelectedSubject={setSelectedSubject}
        onClose={handleClose}
        loading={loading}
      />

      <EditOffcanvas
        show={showEdit}
        onClose={handleClose}
        topics={topicTitle}
        setTopics={setTopicTitle}
        selectedSubject={selectedSubject}
        setSelectedSubject={setSelectedSubject}
        subjects={subjects}
        updateTopics={updateTopics}
        topicsId={topicId}
        loading={loading}
      />

      <ToastContainer position="top-right" autoClose={5000} />
    </>
  );
}
